import { Box, Typography, Paper } from '@mui/material';
import TitleIcon from '@mui/icons-material/Title';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FormTextField } from '@/shared/components/forms';

/**
 * Title section component for the offer creation form.
 * Allows users to enter a custom title for their offer.
 *
 * Features:
 * - Required title field (max 120 characters)
 * - Client-side validation with error messages
 * - Placeholder text example
 * - Mobile-optimized with proper validation
 */

export function TitleSection() {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '16px',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TitleIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('publish.offerTitle.label')}
        </Typography>
      </Box>

      {/* Title Input */}
      <Box sx={{ mb: 0 }}>
        <Controller
          name="title"
          control={control}
          rules={{
            required: t('publish.offerTitle.required'),
            validate: (value: string) =>
              value?.trim().length > 0 || t('publish.offerTitle.required'),
            maxLength: {
              value: 120,
              message: t('publish.offerTitle.maxLength'),
            },
          }}
          render={({ field }) => (
            <FormTextField
              {...field}
              label={t('publish.offerTitle.label')}
              placeholder={t('publish.offerTitle.placeholder')}
              required
              error={!!errors.title}
              helperText={errors.title?.message as string}
              fullWidth
              inputProps={{
                maxLength: 120,
                'data-testid': 'publish-title-input',
              }}
              data-testid="publish-title-field"
            />
          )}
        />
      </Box>
    </Paper>
  );
}
